import React, { useState, useEffect, useRef } from "react";
import { getClients, createDevis, getSectionTypes, getSousSections, getItems, getSousItems } from "./api";
import "./CreateDevis.css";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, BorderStyle, AlignmentType } from "docx";
import {FilePdf,FileDoc,FloppyDisk,ArrowUUpLeft,CaretRight,CaretDown} from "@phosphor-icons/react";
import Alert from "./components/Alert";
import LoadingProgress from "./components/LoadingProgress";
function CreateDevis() {
  const [clients, setClients] = useState([]);
  const [sectionTypes, setSectionTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  
  // États pour le chargement
  const [isLoadingPDF, setIsLoadingPDF] = useState(false);
  const [isLoadingWord, setIsLoadingWord] = useState(false);
  
  // Ref pour la génération du PDF
  const previewRef = useRef(null);

  // États pour le formulaire progressif
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedSousSections, setSelectedSousSections] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedSousItems, setSelectedSousItems] = useState([]);

  // États pour les données chargées
  const [sousSections, setSousSections] = useState({});
  const [items, setItems] = useState({});
  const [sousItems, setSousItems] = useState({});

  // États pour l'expansion/collapse (séparés de la sélection)
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [expandedSousSections, setExpandedSousSections] = useState(new Set());
  const [expandedItems, setExpandedItems] = useState(new Set());

  // États pour les remarques et unités
  const [remarques, setRemarques] = useState({
    sections: {},
    sousSections: {},
    items: {},
    sousItems: {},
    global: ""
  });

  const [unites, setUnites] = useState({});

  // État pour les valeurs des sous-sections spéciales
  const [sousSectionValues, setSousSectionValues] = useState({});

  // Fonction pour gérer les valeurs des sous-sections spéciales
  const handleSousSectionValueChange = (sousSectionId, value) => {
    setSousSectionValues(prev => ({
      ...prev,
      [sousSectionId]: value
    }));
  };

  // Fonction pour obtenir la valeur d'une sous-section
  const getSousSectionValue = (sousSectionId) => {
    return sousSectionValues[sousSectionId] || "";
  };

  // Fonction pour gérer les remarques
  const handleRemarqueChange = (type, id, value) => {
    setRemarques(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [id]: value
      }
    }));
  };

  // Fonction pour obtenir la remarque d'un élément
  const getRemarque = (type, id) => {
    return remarques[type]?.[id] || "";
  };

  // État pour l'aperçu
  const [showPreview, setShowPreview] = useState(false);
  const [selectedElements, setSelectedElements] = useState([]);

  // État pour l'en-tête du devis
  const [devisData, setDevisData] = useState({
    reference: "",
    date: new Date().toISOString().split('T')[0],
    destinataire: "",
    societe: "",
    telephone: "",
    vRef: "",
    objet: "",
    nombrePages: 1
  });

  // État pour la recherche de clients
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [filteredClients, setFilteredClients] = useState([]);

  // État pour suivre l'étape actuelle du formulaire
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    loadClients();
    loadSectionTypes();
  }, []);

  // Effet pour filtrer les clients
  useEffect(() => {
    if (clientSearchTerm.trim() === "") {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client =>
        client.nom_entreprise.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
        (client.contact && client.contact.toLowerCase().includes(clientSearchTerm.toLowerCase())) ||
        (client.telephone && client.telephone.includes(clientSearchTerm)) ||
        (client.email && client.email.toLowerCase().includes(clientSearchTerm.toLowerCase())) ||
        (client.reference_client && client.reference_client.toLowerCase().includes(clientSearchTerm.toLowerCase()))
      );
      setFilteredClients(filtered);
    }
  }, [clients, clientSearchTerm]);

  const loadClients = async () => {
    try {
      const token = localStorage.getItem("token");
      const clientsData = await getClients(token);
      setClients(clientsData);
      setFilteredClients(clientsData); // Initialize filteredClients with all clients
    } catch (error) {
      console.error("Erreur lors du chargement des clients:", error);
    }
  };

  const loadSectionTypes = async () => {
    try {
      const token = localStorage.getItem("token");
      const sectionsData = await getSectionTypes(token);

      // Ordonner les sections selon l'ordre souhaité
      const normalize = (text) =>
        (text || "")
          .toString()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[-']/g, ' ')
          .toLowerCase();

      const desiredOrder = [
        'etendue des travaux',
        'controle qualite hse',
        'bordereau de prix',
        'delai de realisation',
        'conditions de paiement',
        "validite de l offre"
      ];

      const getOrderIndex = (description) => {
        const d = normalize(description);
        // Cas robustes pour Qualité-HSE
        if (d.includes('qualite') && d.includes('hse')) return 1;
        for (let i = 0; i < desiredOrder.length; i += 1) {
          if (d.includes(desiredOrder[i])) return i;
        }
        return desiredOrder.length + 1; // à la fin si non trouvé
      };

      const sortedSections = [...sectionsData].sort((a, b) => {
        const ai = getOrderIndex(a.description);
        const bi = getOrderIndex(b.description);
        if (ai !== bi) return ai - bi;
        // fallback alphabétique
        return normalize(a.description).localeCompare(normalize(b.description));
      });

      setSectionTypes(sortedSections);
    } catch (error) {
      console.error("Erreur lors du chargement des sections:", error);
    }
  };

  const handleClientSelect = async (client) => {
    setSelectedClient(client);
    setSelectedSections([]);
    setSelectedSousSections([]);
    setSelectedItems([]);
    setSelectedSousItems([]);
    setSousSections({});
    setItems({});
    setSousItems({});
    
    // Pré-remplir l'en-tête
    setDevisData({
      ...devisData,
      destinataire: client.contact ? `Mr. ${client.contact}` : "",
      societe: client.nom_entreprise || "",
      telephone: client.telephone || "",
      vRef: client.reference_client || "",
      reference: devisData.reference || generateReference()
    });
    setCurrentStep(2); // Move to next step
  };

  const handleSectionToggle = async (section) => {
    const isSelected = selectedSections.some(s => s.id === section.id);
    
    if (isSelected) {
      // Désélectionner la section et tous ses enfants
      setSelectedSections(prev => prev.filter(s => s.id !== section.id));
      setSelectedSousSections(prev => prev.filter(ss => !ss.parentSection || ss.parentSection.id !== section.id));
      setSelectedItems(prev => prev.filter(item => !item.parentSection || item.parentSection.id !== section.id));
      setSelectedSousItems(prev => prev.filter(si => !si.parentSection || si.parentSection.id !== section.id));
      
      // Collapse la section quand elle est désélectionnée
      setExpandedSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(section.id);
        return newSet;
      });
    } else {
      // Sélectionner la section
      setSelectedSections(prev => [...prev, section]);
      
      // Auto-expand la section quand elle est sélectionnée
      setExpandedSections(prev => {
        const newSet = new Set(prev);
        newSet.add(section.id);
        return newSet;
      });
      
      // Charger les sous-sections si pas encore fait
      if (!sousSections[section.id]) {
        try {
          const token = localStorage.getItem("token");
          const sousSectionsData = await getSousSections(token, section.id);
          setSousSections(prev => ({
            ...prev,
            [section.id]: sousSectionsData
          }));
        } catch (error) {
          console.error("Erreur lors du chargement des sous-sections:", error);
        }
      }
    }
  };

  const handleSousSectionToggle = async (sousSection, parentSection) => {
    const isSelected = selectedSousSections.some(ss => ss.id === sousSection.id);
    
    if (isSelected) {
      // Désélectionner la sous-section et tous ses enfants
      setSelectedSousSections(prev => prev.filter(ss => ss.id !== sousSection.id));
      setSelectedItems(prev => prev.filter(item => !item.parentSousSection || item.parentSousSection.id !== sousSection.id));
      setSelectedSousItems(prev => prev.filter(si => !si.parentSousSection || si.parentSousSection.id !== sousSection.id));
      
      // Collapse la sous-section quand elle est désélectionnée
      setExpandedSousSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(sousSection.id);
        return newSet;
      });
      
      // Réinitialiser les données de prix pour cette sous-section
      setPriceData(prev => {
        const newData = { ...prev };
        delete newData[sousSection.id];
        return newData;
      });
    } else {
      // Sélectionner la sous-section
      setSelectedSousSections(prev => [...prev, { ...sousSection, parentSection }]);
      
      // Auto-expand la sous-section quand elle est sélectionnée
      setExpandedSousSections(prev => {
        const newSet = new Set(prev);
        newSet.add(sousSection.id);
        return newSet;
      });
      
      // Charger les items si pas encore fait (seulement pour les sections non-prix et non-spéciales)
      const isPriceSection = parentSection.description.includes('Bordereau de prix') || parentSection.description.includes('Prix');
      const isSpecialSection = parentSection.description.includes('Délai de réalisation') || 
                              parentSection.description.includes('Conditions de paiement') || 
                              parentSection.description.includes('Validité de l\'offre');
      if (!isPriceSection && !isSpecialSection && !items[sousSection.id]) {
        try {
          const token = localStorage.getItem("token");
          const itemsData = await getItems(token, sousSection.id);
          setItems(prev => ({
            ...prev,
            [sousSection.id]: itemsData
          }));
        } catch (error) {
          console.error("Erreur lors du chargement des items:", error);
        }
      }
    }
  };

  const handleItemToggle = async (item, parentSousSection, parentSection) => {
    const isSelected = selectedItems.some(i => i.id === item.id);
    
    if (isSelected) {
      // Désélectionner l'item et tous ses enfants
      setSelectedItems(prev => prev.filter(i => i.id !== item.id));
      setSelectedSousItems(prev => prev.filter(si => !si.parentItem || si.parentItem.id !== item.id));
      
      // Collapse l'item quand il est désélectionné
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    } else {
      // Sélectionner l'item
      setSelectedItems(prev => [...prev, { ...item, parentSousSection, parentSection }]);
      
      // Auto-expand l'item quand il est sélectionné
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        newSet.add(item.id);
        return newSet;
      });
      
      // Charger les sous-items si pas encore fait
      if (!sousItems[item.id]) {
        try {
          const token = localStorage.getItem("token");
          const sousItemsData = await getSousItems(token, item.id);
          setSousItems(prev => ({
            ...prev,
            [item.id]: sousItemsData
          }));
        } catch (error) {
          console.error("Erreur lors du chargement des sous-items:", error);
        }
      }
    }
  };

  const handleSousItemToggle = (sousItem, parentItem, parentSousSection, parentSection) => {
    const isSelected = selectedSousItems.some(si => si.id === sousItem.id);
    
    if (isSelected) {
      setSelectedSousItems(prev => prev.filter(si => si.id !== sousItem.id));
    } else {
      setSelectedSousItems(prev => [...prev, { ...sousItem, parentItem, parentSousSection, parentSection }]);
    }
  };

  // Handlers pour l'expansion/collapse (séparés de la sélection)
  const handleSectionExpand = async (section) => {
    const isExpanded = expandedSections.has(section.id);
    
    if (isExpanded) {
      // Collapse
      setExpandedSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(section.id);
        return newSet;
      });
    } else {
      // Expand
      setExpandedSections(prev => {
        const newSet = new Set(prev);
        newSet.add(section.id);
        return newSet;
      });
      
      // Charger les sous-sections si pas encore fait
      if (!sousSections[section.id]) {
        try {
          const token = localStorage.getItem("token");
          const sousSectionsData = await getSousSections(token, section.id);
          setSousSections(prev => ({
            ...prev,
            [section.id]: sousSectionsData
          }));
        } catch (error) {
          console.error("Erreur lors du chargement des sous-sections:", error);
        }
      }
    }
  };

  const handleSousSectionExpand = async (sousSection, parentSection) => {
    const isExpanded = expandedSousSections.has(sousSection.id);
    
    if (isExpanded) {
      // Collapse
      setExpandedSousSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(sousSection.id);
        return newSet;
      });
    } else {
      // Expand
      setExpandedSousSections(prev => {
        const newSet = new Set(prev);
        newSet.add(sousSection.id);
        return newSet;
      });
      
      // Charger les items si pas encore fait (seulement pour les sections non-prix et non-spéciales)
      const isPriceSection = parentSection.description.includes('Bordereau de prix') || parentSection.description.includes('Prix');
      const isSpecialSection = parentSection.description.includes('Délai de réalisation') || 
                              parentSection.description.includes('Conditions de paiement') || 
                              parentSection.description.includes('Validité de l\'offre');
      if (!isPriceSection && !isSpecialSection && !items[sousSection.id]) {
        try {
          const token = localStorage.getItem("token");
          const itemsData = await getItems(token, sousSection.id);
          setItems(prev => ({
            ...prev,
            [sousSection.id]: itemsData
          }));
        } catch (error) {
          console.error("Erreur lors du chargement des items:", error);
        }
      }
    }
  };

  const handleItemExpand = async (item, parentSousSection, parentSection) => {
    const isExpanded = expandedItems.has(item.id);
    
    if (isExpanded) {
      // Collapse
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    } else {
      // Expand
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        newSet.add(item.id);
        return newSet;
      });
      
      // Charger les sous-items si pas encore fait
      if (!sousItems[item.id]) {
        try {
          const token = localStorage.getItem("token");
          const sousItemsData = await getSousItems(token, item.id);
          setSousItems(prev => ({
            ...prev,
            [item.id]: sousItemsData
          }));
        } catch (error) {
          console.error("Erreur lors du chargement des sous-items:", error);
        }
      }
    }
  };



  const handleUniteChange = (sousItemId, value) => {
    setUnites(prev => ({
      ...prev,
      [sousItemId]: value
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDevisData({
      ...devisData,
      [name]: value
    });
  };

  const generateReference = () => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `L${year}${month}${random}`;
  };

  const handleGenerateReference = () => {
    setDevisData({
      ...devisData,
      reference: generateReference()
    });
  };

  const handleValider = () => {
    if (!selectedClient) {
      setMessage("Veuillez sélectionner un client");
      return;
    }

    if (selectedSections.length === 0) {
      setMessage("Veuillez sélectionner au moins une section");
      return;
    }

    // Créer la liste des éléments sélectionnés
    const elements = [];
    
    // S'assurer que les sections sélectionnées suivent l'ordre souhaité dans l'aperçu
    const normalize = (text) =>
      (text || "")
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[-']/g, ' ')
        .toLowerCase();

    const desiredOrder = [
      'etendue des travaux',
      'controle qualite hse',
      'bordereau de prix',
      'delai de realisation',
      'conditions de paiement',
      "validite de l offre"
    ];

    const getOrderIndex = (description) => {
      const d = normalize(description);
      if (d.includes('qualite') && d.includes('hse')) return 1;
      for (let i = 0; i < desiredOrder.length; i += 1) {
        if (d.includes(desiredOrder[i])) return i;
      }
      return desiredOrder.length + 1;
    };

    const sortedSelectedSections = [...selectedSections].sort((a, b) => {
      const ai = getOrderIndex(a.description);
      const bi = getOrderIndex(b.description);
      if (ai !== bi) return ai - bi;
      return normalize(a.description).localeCompare(normalize(b.description));
    });

    sortedSelectedSections.forEach(section => {
      const sectionElement = {
        type: 'section',
        data: section,
        remarque: remarques.sections[section.id] || ""
      };
      elements.push(sectionElement);

      // Ajouter les sous-sections de cette section
      const sectionSousSections = selectedSousSections.filter(ss => ss.parentSection && ss.parentSection.id === section.id);
      sectionSousSections.forEach(sousSection => {
        const sousSectionElement = {
          type: 'sousSection',
          data: sousSection,
          remarque: remarques.sousSections[sousSection.id] || "",
          parentSection: section,
          priceData: priceData[sousSection.id] || null,
          value: sousSectionValues[sousSection.id] || ""
        };
        elements.push(sousSectionElement);

        // Ajouter les items de cette sous-section (seulement pour les sections non-prix et non-spéciales)
        const isPriceSection = section.description.includes('Bordereau de prix') || section.description.includes('Prix');
        const isSpecialSection = section.description.includes('Délai de réalisation') || 
                                section.description.includes('Conditions de paiement') || 
                                section.description.includes('Validité de l\'offre');
        if (!isPriceSection && !isSpecialSection) {
          const sousSectionItems = selectedItems.filter(item => item.parentSousSection && item.parentSousSection.id === sousSection.id);
          sousSectionItems.forEach(item => {
            const itemElement = {
              type: 'item',
              data: item,
              remarque: remarques.items[item.id] || "",
              parentSousSection: sousSection,
              parentSection: section
            };
            elements.push(itemElement);

            // Ajouter les sous-items de cet item
            const itemSousItems = selectedSousItems.filter(si => si.parentItem && si.parentItem.id === item.id);
            itemSousItems.forEach(sousItem => {
              const sousItemElement = {
                type: 'sousItem',
                data: sousItem,
                remarque: remarques.sousItems[sousItem.id] || "",
                unite: unites[sousItem.id] || "",
                parentItem: item,
                parentSousSection: sousSection,
                parentSection: section
              };
              elements.push(sousItemElement);
            });
          });
        }
      });
    });

    setSelectedElements(elements);
    setShowPreview(true);
    setCurrentStep(5); // Set to Preview step
  };

  const handleSaveDevis = async () => {
    if (!selectedClient) {
      setMessage("Veuillez sélectionner un client");
      return;
    }

    if (!devisData.reference || !devisData.objet) {
      setMessage("Veuillez remplir la référence et l'objet du devis");
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const devisToSave = {
        ...devisData,
        client_id: selectedClient.id,
        elements: selectedElements,
        remarques: remarques,
        unites: unites,
        priceData: priceData,
        sousSectionValues: sousSectionValues
      };

      const result = await createDevis(token, devisToSave);
      
      if (result.message) {
        setMessage("Devis créé avec succès !");
        // Réinitialiser le formulaire
        setDevisData({
          reference: "",
          date: new Date().toISOString().split('T')[0],
          destinataire: "",
          societe: "",
          telephone: "",
          vRef: "",
          objet: "",
          nombrePages: 1
        });
        setSelectedClient(null);
        setSelectedSections([]);
        setSelectedSousSections([]);
        setSelectedItems([]);
        setSelectedSousItems([]);
        setSousSections({});
        setItems({});
        setSousItems({});
        setRemarques({
          sections: {},
          sousSections: {},
          items: {},
          sousItems: {},
          global: ""
        });
        setUnites({});
        setPriceData({});
        setSousSectionValues({});
        setSelectedElements([]);
        setShowPreview(false);
      } else {
        setMessage("Erreur lors de la création du devis");
      }
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      setMessage("Erreur lors de la création du devis");
    } finally {
      setSaving(false);
    }
  };

  // États pour les prix
  const [priceData, setPriceData] = useState({});

  const handlePriceInputChange = (elementId, field, value) => {
    setPriceData(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const calculateTotal = (elementId) => {
    const data = priceData[elementId];
    if (data && data.qt && data.pu) {
      return (data.qt * data.pu).toFixed(2);
    }
    return '0.00';
  };

  const renderSectionContent = (element) => {
    // Fonction helper pour détecter le type de section parent
    const getSectionType = (element) => {
      if (element.type === 'section') {
        return element.data.description;
      }
      if (element.parentSection) {
        return element.parentSection.description;
      }
      return '';
    };

    const sectionType = getSectionType(element);
    
    // Style pour "Contrôle Qualité - HSE"
    if (sectionType.includes('Contrôle Qualité') || sectionType.includes('HSE')) {
      return (
        <div key={element.id || Math.random()} className="section-element quality-hse">
          {element.type === 'section' && (
            <div className="section-item">
              <h4>{element.data.description}</h4>
              {getRemarque('sections', element.data.id) && (
                <div className="remarque-section-red">
                  <strong>Remarque:</strong> {getRemarque('sections', element.data.id)}
                </div>
              )}
            </div>
          )}
          
          {element.type === 'sousSection' && (
            <div className="sous-section-item">
              <div className="sous-section-title">
                <span className="bullet">•</span>
                {element.data.description}
              </div>
              {getRemarque('sousSections', element.data.id) && (
                <div className="remarque-section-red">
                  <strong>Remarque:</strong> {getRemarque('sousSections', element.data.id)}
                </div>
              )}
            </div>
          )}
          
          {element.type === 'item' && (
            <div className="item-element">
              <div className={`item ${element.data.type_valeur || ''}`}>
                <span className="bullet">-</span>
                {element.data.description}
              </div>
              {getRemarque('items', element.data.id) && (
                <div className="remarque-section-red">
                  <strong>Remarque:</strong> {getRemarque('items', element.data.id)}
                </div>
              )}
            </div>
          )}

          {element.type === 'sousItem' && (
            <div className="sous-item-element">
              <div className={`sous-item ${element.data.type_valeur || ''}`}>
                <span className="indent">--</span>
                {element.data.description}
                {element.unite && <span className="unite"> ................................................................................................................ {element.unite} µm</span>}
              </div>
              {getRemarque('sousItems', element.data.id) && (
                <div className="remarque-section-red">
                  <strong>Remarque:</strong> {getRemarque('sousItems', element.data.id)}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    // Style pour "Bordereau de prix"
    if (sectionType.includes('Bordereau de prix') || sectionType.includes('Prix')) {
      return (
        <div key={element.id || Math.random()} className="section-element">
          {element.type === 'section' && (
            <div className="section-item">
              <h4>{element.data.description}</h4>
              
              
                             {/* Afficher le tableau seulement pour la section, pas pour les sous-sections */}
               <div className="price-table-container">
                 <table className="price-table">
                  <thead>
                    <tr>
                      <th>Désignation</th>
                      <th>Unité</th>
                      <th>QT</th>
                      <th>PU DHS HT</th>
                      <th>PT DHS HT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSousSections
                      .filter(ss => ss.parentSection && ss.parentSection.description.includes('Bordereau de prix'))
                      .map(sousSection => {
                        const priceInfo = priceData[sousSection.id];
                        const qt = priceInfo?.qt || 0;
                        const pu = priceInfo?.pu || 0;
                        const total = qt * pu;
                        
                        return (
                          <tr key={sousSection.id}>
                            <td>{sousSection.description}</td>
                            <td>{sousSection.unite || 'M²'}</td>
                            <td>{qt.toFixed(2)}</td>
                            <td>{pu.toFixed(2)}</td>
                            <td className="price-total">{total.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                
                
              </div>
              
              {/* Afficher la remarque en bas du tableau */}
              {getRemarque('sections', element.data.id) && (
                <div className="remarque-section-red" style={{ marginTop: '20px' }}>
                  <strong>(*)</strong> {getRemarque('sections', element.data.id)}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    // Style pour les sections spéciales (Délai de réalisation, Conditions de paiement, Validité de l'offre)
    if (sectionType.includes('Délai de réalisation') || sectionType.includes('Conditions de paiement') || sectionType.includes('Validité de l\'offre')) {
      return (
        <div key={element.id || Math.random()} className="section-element special-section">
          {element.type === 'section' && (
            <div className="section-item">
              <h4>{element.data.description}</h4>
              {getRemarque('sections', element.data.id) && (
                <div className="remarque-section-red">
                  <strong>Remarque:</strong> {getRemarque('sections', element.data.id)}
                </div>
              )}
            </div>
          )}
          
          {element.type === 'sousSection' && (
            <div className="sous-section-item">
              <div className="sous-section-title-with-value">
                <span className="bullet">•</span>
                <span className="sous-section-text">{element.data.description}</span>
                                 <span className="sous-section-value">  {element.value || getSousSectionValue(element.data.id)};</span>
              </div>
              {getRemarque('sousSections', element.data.id) && (
                <div className="remarque-section-red">
                  <strong>Remarque:</strong> {getRemarque('sousSections', element.data.id)}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    // Style par défaut pour "Etendue des travaux" et autres
    return (
      <div key={element.id || Math.random()} className="section-element work-scope">
        {element.type === 'section' && (
          <div className="section-item">
            <h4>{element.data.description}</h4>
            {getRemarque('sections', element.data.id) && (
              <div className="remarque-section-red">
                <strong>Remarque:</strong> {getRemarque('sections', element.data.id)}
              </div>
            )}
          </div>
        )}
        
        {element.type === 'sousSection' && (
          <div className="sous-section-item">
            <div className={`sous-section-title ${element.data.description.includes('À notre charge') || element.data.description.includes('À votre charge') ? 'charge-section' : ''}`}>
              {element.data.description.includes('À notre charge') || element.data.description.includes('À votre charge') ? (
                <span className="charge-title">{element.data.description}</span>
              ) : (
                <>
                  <span className="arrow">➤</span>
                  {element.data.description}
                </>
              )}
            </div>
            {getRemarque('sousSections', element.data.id) && (
              <div className="remarque-section-red">
                <strong>Remarque:</strong> {getRemarque('sousSections', element.data.id)}
              </div>
            )}
          </div>
        )}
        
        {element.type === 'item' && (
          <div className="item-element">
            <div className={`item ${element.data.type_valeur || ''}`}>
              <span className="bullet">●</span>
              {element.data.description}
            </div>
            {getRemarque('items', element.data.id) && (
              <div className="remarque-section-red">
                <strong>Remarque:</strong> {getRemarque('items', element.data.id)}
              </div>
            )}
          </div>
        )}

        {element.type === 'sousItem' && (
          <div className="sous-item-element">
            <div className={`sous-item ${element.data.type_valeur || ''}`}>
              <span className="indent">-</span>
              {element.data.description}
              {element.unite && <span className="unite"> ................................................................................................................ {element.unite} µm</span>}
            </div>
            {getRemarque('sousItems', element.data.id) && (
              <div className="remarque-section-red">
                <strong>Remarque:</strong> {getRemarque('sousItems', element.data.id)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleRetour = () => {
    setShowPreview(false);
    setCurrentStep(4); // Set back to Header step
  };

  // Génération Word (.docx) avec docx
  const generateDOCX = async () => {
    try {
      setIsLoadingWord(true);
      const brandBlue = "0B3B7A";
      
      // Debug: Afficher les données pour vérifier
      console.log("=== DEBUG GENERATION WORD ===");
      console.log("devisData:", devisData);
      console.log("selectedClient:", selectedClient);
      console.log("selectedElements:", selectedElements);

      // Fonction helper pour créer des paragraphes avec label et valeur (non utilisée pour le moment)
      // const makeLabelLine = (label, value) => new Paragraph({
      //   children: [
      //     new TextRun({ text: `${label}: `, bold: true, color: "111111", size: 24 }),
      //     new TextRun({ text: value || "", color: "111111", size: 24 })
      //   ],
      //   spacing: { after: 120 }
      // });

             // Fonction utilitaire pour cellules
       const labelCell = (text, options = {}) =>
         new TableCell({
           children: [
             new Paragraph({
               children: [new TextRun({ text, bold: true, size: 22 })],
             })
           ],
           width: { size: options.width || 25, type: WidthType.PERCENTAGE },
           shading: options.shading || undefined,
         });

       const valueCell = (text, options = {}) => {
         const textValue = text !== null && text !== undefined ? String(text) : "";
         return new TableCell({
           children: [
             new Paragraph({
               children: [new TextRun({ text: textValue, size: 22 })],
             })
           ],
           width: { size: options.width || 25, type: WidthType.PERCENTAGE },
         });
       };

      // Fonction pour générer le contenu des sections
      const generateSectionContent = (element) => {
        const getSectionType = (element) => {
          if (element.type === 'section') {
            return element.data.description;
          }
          if (element.parentSection) {
            return element.parentSection.description;
          }
          return '';
        };

        const sectionType = getSectionType(element);
        const children = [];

        // Style pour "Contrôle Qualité - HSE"
        if (sectionType.includes('Contrôle Qualité') || sectionType.includes('HSE')) {
        if (element.type === 'section') {
            children.push(new Paragraph({
              children: [new TextRun({ text: element.data.description, color: brandBlue, bold: true, size: 28 })],
              spacing: { after: 240,before :240 },
              border: {
                bottom: {  space: 1, value: "single", size: 6 }
              },

              
              
            }));
            
            
            const remarque = getRemarque('sections', element.data.id);
            if (remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 }
              }));
            }
          }
          
        if (element.type === 'sousSection') {
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "• " }),
                new TextRun({ text: element.data.description, bold: true })
              ],
              spacing: { after: 120 },
              indent : {left:120}

            }));
            
            const remarque = getRemarque('sousSections', element.data.id);
            if (remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 }
              }));
            }
          }
          
        if (element.type === 'item') {
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "- " }),
                new TextRun({ text: element.data.description })
              ],
              spacing: { after: 120 },
              indent: { left: 720 }
            }));
            
            const remarque = getRemarque('items', element.data.id);
            if (remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 },
                indent: { left: 720 }
              }));
            }
          }

        if (element.type === 'sousItem') {
            const unite = element.unite ? ` .................................................... ${element.unite} µm` : '';
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "-- ", color: brandBlue }),
                new TextRun({ text: element.data.description + unite })
              ],
              spacing: { after: 120 },
              indent: { left: 1440 }
            }));
            
            const remarque = getRemarque('sousItems', element.data.id);
            if (remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 },
                indent: { left: 1440 }
              }));
            }
          }
        }
        
        // Style pour "Bordereau de prix"
        else if (sectionType.includes('Bordereau de prix') || sectionType.includes('Prix')) {
          if (element.type === 'section') {
            children.push(new Paragraph({
              children: [new TextRun({ text: element.data.description, color: brandBlue, bold: true, size: 28 })],
              spacing: { after: 240,before :240 }   //+
            }));
            
                         // Tableau de prix avec le même style que l'en-tête
            const prixSousSections = selectedSousSections.filter(ss => 
              ss.parentSection && ss.parentSection.description && 
              (ss.parentSection.description.includes('Bordereau de prix') || ss.parentSection.description.includes('Prix'))
            );

            if (prixSousSections.length > 0) {
              const tableRows = [
                // En-tête du tableau avec style bleu
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: "Désignation", bold: true, color: "FFFFFF", size: 22 })]
                        })
                      ],
                      shading: { fill: brandBlue },
                      alignment: AlignmentType.CENTER,
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: "Unité", bold: true, color: "FFFFFF", size: 22 })]
                        })
                      ],
                      shading: { fill: brandBlue },alignment: AlignmentType.CENTER,
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: "QT", bold: true, color: "FFFFFF", size: 22 })]
                        })
                      ],
                      shading: { fill: brandBlue }
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: "PU DHS HT", bold: true, color: "FFFFFF", size: 22 })]
                        })
                      ],
                      shading: { fill: brandBlue },alignment: AlignmentType.CENTER,
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: "PT DHS HT", bold: true, color: "FFFFFF", size: 22 })]
                        })
                      ],
                      shading: { fill: brandBlue },alignment: AlignmentType.CENTER,
                      
                    })
                  ]
                })
              ];

              // Lignes de données
              prixSousSections.forEach(sousSection => {
                const priceInfo = priceData[sousSection.id] || {};
                const qt = Number(priceInfo.qt || 0);
                const pu = Number(priceInfo.pu || 0);
                const total = qt * pu;
                
                tableRows.push(new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: sousSection.data?.description || sousSection.description || "", size: 22 })]
                        })
                      ]
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: sousSection.unite || 'M²', size: 22 })]
                        })
                      ]
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: qt.toFixed(2), size: 22 })]
                        })
                      ]
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: pu.toFixed(2), size: 22 })]
                        })
                      ]
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: total.toFixed(2), bold: true, color: brandBlue, size: 22 })]
                        })
                      ]
                    })
                  ]
                }));
              });

                             children.push(new Table({
                 rows: tableRows,
                 width: { size: 100, type: WidthType.PERCENTAGE },
                 borders: {
                   top: { style: BorderStyle.SINGLE, size: 1,  },
                   bottom: { style: BorderStyle.SINGLE, size: 1,  },
                   left: { style: BorderStyle.SINGLE, size: 1,  },
                   right: { style: BorderStyle.SINGLE, size: 1,  },
                   insideHorizontal: { style: BorderStyle.SINGLE, size: 1,  },
                   insideVertical: { style: BorderStyle.SINGLE, size: 1,  },
                 }
               }));
               
               // Afficher la remarque en bas du tableau
               const remarque = getRemarque('sections', element.data.id);
               if (remarque) {
                 children.push(new Paragraph({
                   children: [new TextRun({ text: `(*) ${remarque}`, italic: true })],
                   spacing: { after: 120 },
                   indent:{left:301}
                 }));
               }
             }
          }
        }
        
        // Style pour les sections spéciales
        else if (sectionType.includes('Délai de réalisation') || sectionType.includes('Conditions de paiement') || sectionType.includes('Validité de l\'offre')) {
          if (element.type === 'section') {
            children.push(new Paragraph({
              children: [new TextRun({ text: element.data.description, color: brandBlue, bold: true, size: 28 })],
              spacing: { after: 240, before :240 }    //+
            }));
            
            const remarque = getRemarque('sections', element.data.id);
            if (remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 }
              }));
            }
          }
          
          if (element.type === 'sousSection') {
            const value = element.value || getSousSectionValue(element.data.id);
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "• ",  }),
                new TextRun({ text: element.data.description, bold: true }),
                new TextRun({ text: `  ${value};`, color: brandBlue, bold: true })
              ],
              spacing: { after: 120 },
              indent:{left:120}
            }));
            
            const remarque = getRemarque('sousSections', element.data.id);
            if (remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 }
              }));
            }
          }
        }
        
        // Style par défaut pour "Etendue des travaux" et autres
        else {
          if (element.type === 'section') {
            children.push(new Paragraph({
              children: [new TextRun({ text: element.data.description, color: brandBlue, bold: true, size: 28 })],
              spacing: { after: 240 ,before :240},
              border: {
                bottom: { color: brandBlue, space: 1, size: 6, value: "single" }
              }
            }));
            
            const remarque = getRemarque('sections', element.data.id);
            if (remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 },
                
              }));
            }
          }
          
          if (element.type === 'sousSection') {
            const isChargeSection = element.data.description.includes('À notre charge') || element.data.description.includes('À votre charge');
            children.push(new Paragraph({
              children: [
                isChargeSection ? 
                  new TextRun({ text: element.data.description,  bold: true }) :
                  new TextRun({ text: "➤ ", color: brandBlue }),
                !isChargeSection && new TextRun({ text: element.data.description, bold: true ,color: brandBlue })
              ].filter(Boolean),
              spacing: { after: 120,before :340 }
            }));
            
            const remarque = getRemarque('sousSections', element.data.id);
            if (remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 }
              }));
            }
          }
          
          if (element.type === 'item') {
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "● ",  }),
                new TextRun({ text: element.data.description })
              ],
              spacing: { after: 120 },
              indent: { left: 720 }
            }));
            
            const remarque = getRemarque('items', element.data.id);
            if (remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 },
                indent: { left: 720 }
              }));
            }
          }

          if (element.type === 'sousItem') {
            const unite = element.unite ? ` .............................................. ${element.unite} µm` : '';
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "- " }),
                new TextRun({ text: element.data.description + unite })
              ],
              spacing: { after: 120 },
              indent: { left: 1440 }
            }));
            
            const remarque = getRemarque('sousItems', element.data.id);
            if (remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 },
                indent: { left: 1440 }
              }));
            }
          }
        }

        return children;
      };

      // Créer le document Word
      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: {
                top: 1440,
                right: 1440,
                bottom: 1440,
                left: 1440
              }
            }
          },
          children: [
            // Titre
            

            // Tableau d'en-tête (identique à l'aperçu)
            (() => {
              // Debug: Vérifier les valeurs avant création du tableau
              console.log("=== DEBUG TABLEAU EN-TÊTE ===");
              console.log("Date:", devisData.date || new Date().toISOString().split('T')[0]);
              console.log("Nombre pages:", String(devisData.nombrePages || "1"));
              console.log("Référence:", devisData.reference || generateReference());
              console.log("Destinataire:", devisData.destinataire || (selectedClient ? `Mr. ${selectedClient.contact || ''}` : ""));
              console.log("Société:", devisData.societe || (selectedClient ? selectedClient.nom_entreprise : ""));
              console.log("Téléphone:", devisData.telephone || (selectedClient ? selectedClient.telephone : ""));
              console.log("V/Réf:", devisData.vRef || (selectedClient ? selectedClient.reference_client : ""));
              console.log("Objet:", devisData.objet || "");
              
              const headerTable = new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1,  },
                  bottom: { style: BorderStyle.SINGLE, size: 1,  },
                  left: { style: BorderStyle.SINGLE, size: 1,  },
                  right: { style: BorderStyle.SINGLE, size: 1,  },
                  insideHorizontal: { style: BorderStyle.SINGLE, size: 1,  },
                  insideVertical: { style: BorderStyle.SINGLE, size: 1,  },
                  
                },
                rows: [
                                     // Ligne 1 : Date + Offre de prix (merge)
                   new TableRow({
                     children: [
                       labelCell("Date"),
                       valueCell(devisData.date),
                       new TableCell({
                         columnSpan: 2,
                         children: [
                           new Paragraph({
                             children: [
                               new TextRun({
                                 text: "OFFRE DE PRIX",
                                 bold: true,
                                 color: "FFFFFF",
                                 size: 28,
                               }),
                             ],
                             alignment: AlignmentType.CENTER,
                           }),
                         ],
                         shading: { fill: brandBlue },
                       }),
                     ],
                   }),
                                     // Ligne 2 : Nombre de pages + N/Réf
                   new TableRow({
                     children: [
                       labelCell("Nombre de pages (celle-ci comprise)"),
                       valueCell(devisData.nombrePages),
                       labelCell("N/Réf."),
                       new TableCell({
                         children: [
                           new Paragraph({
                             children: [
                               new TextRun({
                                 text: devisData.reference || "",
                                 bold: true,
                                 color: brandBlue,
                                 size: 22,
                               })
                             ],alignment: AlignmentType.CENTER,
                           }),
                         ],
                       }),
                     ],
                   }),
                                     // Ligne 3 : Destinataire
                   new TableRow({
                     children: [
                       labelCell("Destinataire"),
                       valueCell(devisData.destinataire),
                     ],
                   }),
                   // Ligne 4 : Société
                   new TableRow({
                     children: [
                       labelCell("Société"),
                       valueCell(devisData.societe),
                     ],
                   }),
                   // Ligne 5 : Téléphone
                   new TableRow({
                     children: [
                       labelCell("Tél"),
                       valueCell(devisData.telephone),
                     ],
                   }),
                   // Ligne 6 : V/Réf.
                   new TableRow({
                     children: [labelCell("V/Réf."), valueCell(devisData.vRef)],
                   }),
                   // Ligne 7 : Objet (merge sur 3 colonnes)
                   new TableRow({
                     children: [
                       labelCell("Objet"),
                       new TableCell({
                         columnSpan: 3,
                         children: [
                           new Paragraph({
                             children: [
                               new TextRun({ 
                                 text: devisData.objet || "", 
                                 size: 22 
                               })
                             ],
                           }),
                         ],
                       }),
                     ],
                   }),
                ],
              });
              return headerTable;
            })(),

            // Salutation
            new Paragraph({ text: "Monsieur,", spacing: { after: 120,before: 240  } }),
            new Paragraph({ 
              text: "Suite à votre demande, nous vous communiquons ci-après nos meilleures conditions pour la réalisation des prestations citées en objet.",
              spacing: { after: 240 },
              indent:{left :300}
              
            }),

            // Contenu des sections
            ...selectedElements.flatMap(element => generateSectionContent(element)),

            // Note de fin
            new Paragraph({ 
              text: "Nous restons à votre entière disposition pour tout renseignement complémentaire et vous prions d'agréer, Monsieur, nos meilleures salutations.",
              spacing: { after: 240,before :240 }
            })
          ]
        }]
      });

      // Générer le document (navigateur)
      const blob = await Packer.toBlob(doc);
      const fileName = `devis_${devisData.destinataire || 'document'}_${devisData.societe }.docx`;
      saveAs(blob, fileName);
      
      console.log("=== FIN DEBUG ===");
      setMessage("Document Word généré avec succès !");
    } catch (err) {
      console.error('Erreur génération DOCX:', err);
      setMessage("Erreur lors de la génération du document Word.");
    } finally {
      setIsLoadingWord(false);
    }
  };

  // Fonctions de navigation
  const goToNextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Fonction pour vérifier si on peut passer à l'étape suivante
  const canGoToNextStep = () => {
    switch (currentStep) {
      case 1:
        return selectedClient !== null;
      case 2:
        return selectedSections.length > 0;
      case 3:
        return selectedSousSections.length > 0;
      case 4:
        return devisData.reference && devisData.objet;
      default:
        return false;
    }
  };

  // Composant indicateur d'étapes
  const StepIndicator = () => {
    const steps = [
      { number: 1, title: "Client", description: "Sélection du client" },
      { number: 2, title: "Sections", description: "Sélection des sections" },
      { number: 3, title: "Détails", description: "Configuration des détails" },
      { number: 4, title: "En-tête", description: "Informations du devis" },
      { number: 5, title: "Aperçu", description: "Prévisualisation" }
    ];

    return (
      <div className="step-indicator">
        {steps.map((step, index) => (
          <div key={step.number} className={`step ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}>
            <div className="step-number">
              {currentStep > step.number ? '✓' : step.number}
            </div>
            <div className="step-content">
              <div className="step-title">{step.title}</div>
              <div className="step-description">{step.description}</div>
            </div>
            {index < steps.length - 1 && (
              <div className={`step-connector ${currentStep > step.number ? 'completed' : ''}`}></div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Composant boutons de navigation
  const NavigationButtons = () => {
    return (
      <div className="navigation-buttons">
        {currentStep > 1 && (
          <button 
            className="btn-secondary" 
            onClick={goToPreviousStep}
          >
            Précédent
          </button>
        )}
        
        {currentStep < 4 && (
          <button 
            className="btn-primary" 
            onClick={goToNextStep}
            disabled={!canGoToNextStep()}
          >
            Suivant
          </button>
        )}
        
        {currentStep === 4 && (
          <button 
            className="btn-primary" 
            onClick={handleValider}
            disabled={!canGoToNextStep()}
          >
            Valider et prévisualiser
          </button>
        )}
      </div>
    );
  };

  // Menubar simple pour l'aperçu
  const PreviewMenubar = () => {
    return (
      <div className="menubar">
        <div className="menubar-menu">
          <button className="menubar-trigger">File</button>
          <div className="menubar-content">
            <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start"}}className="menubar-item" onClick={generatePDF}><FilePdf size={15} color="#212121" weight="duotone" /> Générer PDF</button>
            <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start"}} className="menubar-item" onClick={generateDOCX}><FileDoc size={15} color="#0d0c0c" weight="duotone" /> Générer Word</button>
          </div>
        </div>
        <div className="menubar-menu">
          <button className="menubar-trigger">save</button>
          <div className="menubar-content">
            <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start"}}className="menubar-item" onClick={handleSaveDevis} disabled={saving}>
            <FloppyDisk size={15} color="#0d0c0c" weight="duotone" /> {saving ? "Enregistrement..." : "Enregistrer"} 
            </button>
            <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start"}} className="menubar-item" onClick={handleRetour}><ArrowUUpLeft size={15} color="#0d0c0c" weight="duotone" /> Retour </button>
          </div>
        </div>
      </div>
    );
  };

  // Génère un HTML indépendant du DOM pour la génération PDF
  const generatePDFHTML = (currentDevisData = devisData) => {
    const brandBlue = "0B3B7A";

    function createCell(content, options = {}) {
      const td = document.createElement("td");
      td.innerText = content || "";
      const setImportant = (el, prop, value) => el.style.setProperty(prop, value, 'important');
      if (options.bold) setImportant(td, 'font-weight', '700');
      if (options.color) setImportant(td, 'color', `#${options.color}`);
      else setImportant(td, 'color', '#111');
      if (options.bgColor) setImportant(td, 'background-color', `#${options.bgColor}`);
      setImportant(td, 'text-align', options.textAlign || 'left');
      if (options.fontSize) setImportant(td, 'font-size', options.fontSize + 'px');
      if (options.colSpan) td.colSpan = options.colSpan;
      if (options.rowSpan) td.rowSpan = options.rowSpan;
      if (options.width) setImportant(td, 'width', typeof options.width === 'number' ? `${options.width}px` : options.width);
      if (options.className) td.className = options.className;
      setImportant(td, 'border', '1px solid black');
      setImportant(td, 'padding', '8px');
      setImportant(td, 'vertical-align', 'middle');
      setImportant(td, 'line-height', '22px');
      setImportant(td, 'background-clip', 'padding-box');
      setImportant(td, 'display', 'table-cell');
      setImportant(td, 'white-space', 'normal');
      setImportant(td, 'word-break', 'break-word');
      return td;
    }

    function createTable(data, className = "", colWidths = []) {
      const table = document.createElement("table");
      table.className = className;
      const setImportant = (el, prop, value) => el.style.setProperty(prop, value, 'important');
      setImportant(table, 'width', '100%');
      setImportant(table, 'border-collapse', 'separate');
      setImportant(table, 'border-spacing', '0');
      setImportant(table, 'margin-bottom', '20px');
      setImportant(table, 'font-family', 'Times New Roman, serif');
      setImportant(table, 'font-size', '16px');
      setImportant(table, 'background-color', '#ffffff');
      setImportant(table, 'display', 'table');

      if (Array.isArray(colWidths) && colWidths.length > 0) {
        const colgroup = document.createElement('colgroup');
        colWidths.forEach(w => {
          const col = document.createElement('col');
          col.style.setProperty('width', typeof w === 'number' ? `${w}%` : w, 'important');
          colgroup.appendChild(col);
        });
        table.appendChild(colgroup);
      }
      data.forEach(rowData => {
        const tr = document.createElement("tr");
        setImportant(tr, 'display', 'table-row');
        rowData.forEach(cellData => { if (cellData) tr.appendChild(createCell(cellData.text, cellData)); });
        table.appendChild(tr);
      });
      return table;
    }

    function createParagraph(text, options = {}) {
      const p = document.createElement("p");
      p.innerText = text;
      if (options.marginTop) p.style.marginTop = options.marginTop + "px";
      if (options.marginBottom) p.style.marginBottom = options.marginBottom + "px";
      if (options.marginLeft) p.style.marginLeft = options.marginLeft + "px";
      if (options.color) p.style.color = `#${options.color}`;
      if (options.italic) p.style.fontStyle = "italic";
      if (options.bold) p.style.fontWeight = "bold";
      if (options.fontSize) p.style.fontSize = options.fontSize + "px";
      return p;
    }

    const container = document.createElement('div');
    container.className = 'devis-container';
    container.style.padding = '20px';
    container.style.backgroundColor = '#ffffff';
    container.style.color = '#111';
    container.style.fontFamily = 'Times New Roman, serif';
    container.style.fontSize = '14px';

    // Reset CSS to avoid global overrides and decorative pseudo-elements
    const resetStyle = document.createElement('style');
    resetStyle.textContent = `
      .devis-container, .devis-container * { box-sizing: border-box !important; }
      .devis-container *::before, .devis-container *::after { content: none !important; }
      .devis-container table { border-collapse: collapse !important; border-spacing: 0 !important; background:#fff !important; width: 100% !important; }
      .devis-container td, .devis-container th { border: 1px solid #000 !important; padding: 5px !important; vertical-align: middle !important; background:#fff !important; }
      .devis-container h4 { margin: 25px 0 10px 0 !important; padding-bottom: 6px !important; border-bottom: 2px solid #0B3B7A !important; }
      .devis-container .offre { background:#0B3B7A !important; color:rgb(255, 255, 255) !important; font-weight:700 !important; text-align:center !important; }
      .devis-container .pdf-header-table { width:100% !important; }
      .devis-container .gr { font-weight:700 !important; color:#0B3B7A !important; }
      .devis-container .p1 { font-weight:700 !important; color:#0B3B7A !important; }
      .devis-container .header-band {  !important; padding: 0px !important; border-radius: 4px !important; }
      .devis-container .devis-header td { height: 20px !important; }
      
       
    `;
    container.appendChild(resetStyle);

    const headerTable = createTable([
      // Ligne 1: Date | date | OFFRE DE PRIX (colSpan 2)
      [
        { text: 'Date', bold: true, fontSize: 14 },
        { text: devisData.date || new Date().toISOString().split('T')[0], fontSize: 14 },
        { text: 'OFFRE DE PRIX', colSpan: 2, textAlign: 'center', bold: true,color: 'FFFFFF', fontSize: 16, className: 'offre'}
      ],
      // Ligne 2: Nombre de pages | valeur | N/Réf. | référence
      [
        { text: 'Nombre de pages (celle-ci comprise)', bold: true, fontSize: 14 },
        { text: String(currentDevisData.nombrePages || '1'), fontSize: 13 },
        { text: 'N/Réf.', bold: true, fontSize: 14, className: 'gr' },
        { text: devisData.reference || generateReference(), textAlign: 'center',fontSize: 14, className: 'p1' }
      ],
      // Ligne 3: Destinataire | valeur (colSpan 3)
      [
        { text: 'Destinataire', bold: true, fontSize: 14 },
        { text: devisData.destinataire || (selectedClient ? `Mr. ${selectedClient.contact || ''}` : ''), fontSize: 13, colSpan: 3 }
      ],
      // Ligne 4: Destinataire | valeur (colSpan 3)
      
      // Ligne 5: Société | valeur (colSpan 3)
      [
        { text: 'Société', bold: true, fontSize: 14 },
        { text: devisData.societe || (selectedClient ? selectedClient.nom_entreprise || '' : ''), fontSize: 13, colSpan: 3 }
      ],
      // Ligne 6: Tél | valeur (colSpan 3)
      [
        { text: 'Tél', bold: true, fontSize: 14 },
        { text: devisData.telephone || '', fontSize: 13, colSpan: 3 }
      ],
      // Ligne 7: V/Réf. | valeur (colSpan 3)
      [
        { text: 'V/Réf.', bold: true, fontSize: 14 },
        { text: devisData.vRef || '', fontSize: 13, colSpan: 3 }
      ],
      // Ligne 8: Objet | valeur (colSpan 3)
      [
        { text: 'Objet', bold: true, fontSize: 14 },
        { text: devisData.objet || '', fontSize: 13, colSpan: 3 }
      ]
    ], 'pdf-header-table', [37, 21, 21, 21]);

    const headerBand = document.createElement('div');
    headerBand.className = 'header-band';
    headerBand.appendChild(headerTable);
    container.appendChild(headerBand);
    container.appendChild(createParagraph('Monsieur,', { marginTop: 20, marginBottom: 10 }));
    container.appendChild(createParagraph("Suite à votre demande, nous vous communiquons ci-après nos meilleures conditions pour la réalisation des prestations citées en objet.", { marginLeft: 20, marginBottom: 20 }));

    const generateSectionContent = (element) => {
      const getSectionType = (el) => { if (el.type === 'section') return el.data.description; if (el.parentSection) return el.parentSection.description; return ''; };
      const sectionType = getSectionType(element);

      if (sectionType.includes('Contrôle Qualité') || sectionType.includes('HSE')) {
        if (element.type === 'section') {
          const h4 = document.createElement('h4');
          h4.textContent = element.data.description;
          h4.style.color = `#${brandBlue}`;
          h4.style.fontWeight = 'bold';
          h4.style.fontSize = '18px';
          h4.style.borderBottom = `2px solid #${brandBlue}`;
          h4.style.marginTop = '25px';
          h4.style.marginBottom = '10px';
          container.appendChild(h4);
          const remarque = getRemarque('sections', element.data.id);
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginTop: 10, color: "d32f2f", italic: true }));
        }
        if (element.type === 'sousSection') {
          container.appendChild(createParagraph(`• ${element.data.description}`, { marginLeft: 20, marginTop: 10 }));
          const remarque = getRemarque('sousSections', element.data.id);
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 20, color: "d32f2f", italic: true }));
        }
        if (element.type === 'item') {
          container.appendChild(createParagraph(`- ${element.data.description}`, { marginLeft: 40, marginTop: 5 }));
          const remarque = getRemarque('items', element.data.id);
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 40, color: "d32f2f", italic: true }));
        }
        if (element.type === 'sousItem') {
          const unite = element.unite ? ` .................................................... ${element.unite} µm` : '';
          container.appendChild(createParagraph(`-- ${element.data.description}${unite}`, { marginLeft: 60, marginTop: 3 }));
          const remarque = getRemarque('sousItems', element.data.id);
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 60, color: "d32f2f", italic: true }));
        }
      } else if (sectionType.includes('Bordereau de prix') || sectionType.includes('Prix')) {
        if (element.type === 'section') {
          const h4 = document.createElement('h4');
          h4.textContent = element.data.description;
          h4.style.color = `#${brandBlue}`;
          h4.style.fontWeight = 'bold';
          h4.style.fontSize = '18px';
          h4.style.marginTop = '25px';
          h4.style.marginBottom = '10px';
          container.appendChild(h4);
          const prixSousSections = selectedSousSections.filter(ss => ss.parentSection && ss.parentSection.description && (ss.parentSection.description.includes('Bordereau de prix') || ss.parentSection.description.includes('Prix')));
          if (prixSousSections.length > 0) {
            const tableRows = [[
              { text: 'Désignation', bgColor: brandBlue, color: 'FFFFFF', bold: true },
              { text: 'Unité', bgColor: brandBlue, color: 'FFFFFF', bold: true },
              { text: 'QT', bgColor: brandBlue, color: 'FFFFFF', bold: true },
              { text: 'PU DHS HT', bgColor: brandBlue, color: 'FFFFFF', bold: true },
              { text: 'PT DHS HT', bgColor: brandBlue, color: 'FFFFFF', bold: true }
            ]];
            prixSousSections.forEach(sousSection => {
              const priceInfo = priceData[sousSection.id] || {};
              const qt = Number(priceInfo.qt || 0);
              const pu = Number(priceInfo.pu || 0);
              const total = qt * pu;
              tableRows.push([
                { text: sousSection.data?.description || sousSection.description || '' },
                { text: sousSection.unite || 'M²' },
                { text: qt.toFixed(2) },
                { text: pu.toFixed(2) },
                { text: total.toFixed(2), bold: true, color: brandBlue }
              ]);
            });
            const priceTable = createTable(tableRows, 'devis-table');
            container.appendChild(priceTable);
          }
          const remarque = getRemarque('sections', element.data.id);
          if (remarque) container.appendChild(createParagraph(`(*) ${remarque}`, { marginTop: 10, marginLeft: 15, italic: true }));
        }
      } else if (sectionType.includes('Délai de réalisation') || sectionType.includes('Conditions de paiement') || sectionType.includes("Validité de l'offre")) {
        if (element.type === 'section') {
          const h4 = document.createElement('h4');
          h4.textContent = element.data.description;
          h4.style.color = `#${brandBlue}`;
          h4.style.fontWeight = 'bold';
          h4.style.fontSize = '18px';
          h4.style.marginTop = '25px';
          h4.style.marginBottom = '10px';
          container.appendChild(h4);
          const remarque = getRemarque('sections', element.data.id);
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginTop: 10, color: "d32f2f", italic: true }));
        }
        if (element.type === 'sousSection') {
          const value = element.value || getSousSectionValue(element.data.id);
          container.appendChild(createParagraph(`• ${element.data.description} :${value};`, { marginLeft: 20, marginTop: 10,italic: true }));
          const remarque = getRemarque('sousSections', element.data.id);
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 20, color: "d32f2f", italic: true }));
        }
      } else {
        if (element.type === 'section') {
          const h4 = document.createElement('h4');
          h4.textContent = element.data.description;
          h4.style.color = `#${brandBlue}`;
          h4.style.fontWeight = 'bold';
          h4.style.fontSize = '18px';
          h4.style.borderBottom = `2px solid #${brandBlue}`;
          h4.style.marginTop = '25px';
          h4.style.marginBottom = '10px';
          container.appendChild(h4);
          const remarque = getRemarque('sections', element.data.id);
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginTop: 10, color: "d32f2f", italic: true }));
        }
        if (element.type === 'sousSection') {
          const isChargeSection = element.data.description.includes('À notre charge') || element.data.description.includes('À votre charge');
          if (isChargeSection) { container.appendChild(createParagraph(element.data.description, { marginTop: 40 , bold: true})); }
          else { container.appendChild(createParagraph(`➤ ${element.data.description}`, { marginTop: 10 ,color: brandBlue})); }
          const remarque = getRemarque('sousSections', element.data.id);
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginTop: 5, color: "d32f2f", italic: true }));
        }
        if (element.type === 'item') {
          container.appendChild(createParagraph(`• ${element.data.description}`, { marginLeft: 20, marginTop: 5 }));
          const remarque = getRemarque('items', element.data.id);
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 20, color: "d32f2f", italic: true }));
        }
        if (element.type === 'sousItem') {
          const unite = element.unite ? ` .............................................. ${element.unite} µm` : '';
          container.appendChild(createParagraph(`- ${element.data.description}${unite}`, { marginLeft: 40, marginTop: 3 }));
          const remarque = getRemarque('sousItems', element.data.id);
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 40, color: "d32f2f", italic: true }));
        }
      }
    };

    selectedElements.forEach(el => generateSectionContent(el));
    container.appendChild(createParagraph("Nous restons à votre entière disposition pour tout renseignement complémentaire et vous prions d'agréer, Monsieur, nos meilleures salutations.", { marginTop: 20, marginBottom: 10 }));
    return container;
  };

  // Fonction pour générer le PDF via html2pdf.js en mode indépendant du DOM
  const generatePDF = async () => {
    try {
      setIsLoadingPDF(true);
      const html2pdf = (await import('html2pdf.js')).default;

      // 1) Premier rendu off-DOM pour connaître la hauteur et déduire le nombre de pages
      const pdfElement = generatePDFHTML();
      pdfElement.style.position = 'absolute';
      pdfElement.style.left = '-9999px';
      pdfElement.style.top = '0';
      document.body.appendChild(pdfElement);

      const elementHeight = pdfElement.scrollHeight; // px
      const A4_HEIGHT_PX = 1123; // ~297mm @ 96dpi
      let pageCount = Math.ceil(elementHeight / A4_HEIGHT_PX);
      if (pageCount < 1) pageCount = 1;

      // 2) Répercuter le nombre de pages dans l'en-tête, attendre le re-render
      await new Promise(resolve => {
        setDevisData(prev => { const updated = { ...prev, nombrePages: pageCount }; setTimeout(resolve, 0); return updated; });
      });
      await new Promise(r => setTimeout(r, 100));

      // 3) Régénérer le HTML final hors DOM
      document.body.removeChild(pdfElement);
      const updatedPdfElement = generatePDFHTML({ ...devisData, nombrePages: pageCount });
      updatedPdfElement.style.fontFamily = 'Times New Roman, serif';
      updatedPdfElement.style.fontSize = '14px';
      updatedPdfElement.style.color = '#111';
      updatedPdfElement.style.margin = '20px';
      updatedPdfElement.style.backgroundColor = '#ffffff';

      const opt = {
        margin: 10,
        filename: `devis_${devisData.destinataire || 'document'}_${devisData.societe}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(updatedPdfElement).save();
      setMessage(`PDF généré avec succès ! Nombre de pages: ${pageCount}`);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setMessage("Erreur lors de la génération du PDF. Vérifiez que html2pdf.js est installé.");
    } finally {
      setIsLoadingPDF(false);
    }
  };

  if (showPreview) {
    return (
      <div className="create-devis-container">
        <div className="preview-header">
          <h2>Aperçu du devis</h2>
          <PreviewMenubar />
        </div>
        
        {/* Aperçu du document */}
        <div className="preview-document" ref={previewRef}>
          <div className="document-layout">
            <div className="main-table">
              <table className="client-info">
                <tr>
                  <td>Date</td>
                  <td>{devisData.date || "2025-08-01"}</td>
                  <td className="offre" rowSpan="1" colSpan="2">OFFRE DE PRIX</td>
                </tr>
                <tr>
                  <td>Nombre de pages (celle-ci comprise)</td>
                  <td>{devisData.nombrePages || "1"}</td>
                  <td rowSpan="1"><span className="gr">N/Réf.</span></td>
                  <td rowSpan="1" className="p1">{devisData.reference || "L25086838"}</td>
                </tr>
                <tr>
                  <td>Destinataire</td>
                  <td>{devisData.destinataire || "Mr. 258888"}</td>
                </tr>
                <tr>
                  <td>Société</td>
                  <td>{devisData.societe || "sidennn"}</td>
                </tr>
                <tr>
                  <td>Tél</td>
                  <td>{devisData.telephone || "0639761349"}</td>
                </tr>
                <tr>
                  <td>V/Réf.</td>
                  <td>{devisData.vRef || "okss"}</td>
                </tr>
                <tr>
                  <td>Objet</td>
                  <td colSpan="3">{devisData.objet || "fafdgsgsdgs"}</td>
                </tr>
              </table>
            </div>
          </div>

          <div className="salutation">
            <p>Monsieur,</p>
            <p>Suite à votre demande, nous vous communiquons ci-après nos meilleures conditions pour la réalisation des prestations citées en objet.</p>
          </div>

          {/* Contenu des sections */}
          <div className="sections-content">
            {selectedElements.map((element, index) => 
              renderSectionContent(element)
            )}
          </div>

          {/* Note de fin */}
          <div className="closing-note">
            <p>Nous restons à votre entière disposition pour tout renseignement complémentaire et vous prions d'agréer, Monsieur, nos meilleures salutations.</p>
          </div>
        </div>

        <Alert 
          message={message} 
          onClose={() => setMessage("")}
        />

        {/* actions remplacées par la menubar dans le header */}
      </div>
    );
  }

  return (
    <div className="create-devis-container">
      <h2>Créer un nouveau devis</h2>
      
      {/* Indicateur d'étapes */}
      <StepIndicator />
      
      {/* Formulaire progressif */}
      <div className="progressive-form">
        {/* Étape 1: Sélection du client */}
        <div className={`form-step ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
          <div className="clientui-titlebar">
            <h3>1. Sélection du client</h3>
            <div className="helper">Choisissez un client à gauche pour voir les détails</div>
          </div>

          <div className="clientui-layout">
            <div className="clientui-card clientui-sidebar">
              <div className="clientui-search">
                <input
                  type="text"
                  placeholder="Rechercher un client… (nom, société, téléphone)"
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                />
              </div>
              <div className="clientui-filters">
                <span className={`clientui-chip ${!clientSearchTerm ? 'active' : ''}`}>Tous</span>
              </div>
              <div className="clientui-list">
                {filteredClients.length > 0 ? (
                  (clientSearchTerm ? filteredClients : filteredClients.slice(0, 5)).map((client) => {
                    const isSel = selectedClient?.id === client.id;
                    const initials = (client.nom_entreprise || client.contact || '?')
                      .split(' ').slice(0,2).map(s=>s[0]).join('').toUpperCase();
                    return (
                      <div
                        key={client.id}
                        className={`clientui-item ${isSel ? 'selected' : ''}`}
                        onClick={() => handleClientSelect(client)}
                      >
                        <div className="clientui-avatar">{initials}</div>
                        <div>
                          <div>{client.contact}</div>
                          <small>{client.nom_entreprise || ''} • {client.telephone || ''}</small>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-results">Aucun client trouvé</div>
                )}
                {!clientSearchTerm && filteredClients.length > 5 && (
                  <div style={{padding:'8px', color:'#6b7280'}}>Affinez la recherche pour voir tous les clients…</div>
                )}
              </div>
            </div>

            <div className="clientui-card clientui-details">
              {selectedClient ? (
                <>
                  <h4 className="section-title">Détails du client</h4>
                  <div className="clientui-detail-card clientui-kv">
                    <div className="key">Nom</div><div>{selectedClient.contact || ''}</div>
                    <div className="key">Société</div><div>{selectedClient.nom_entreprise || ''}</div>
                    <div className="key">Téléphone</div><div>{selectedClient.telephone || ''}</div>
                    <div className="key">Email</div><div>{selectedClient.email || ''}</div>
                    <div className="key">Référence</div><div>{selectedClient.reference_client || ''}</div>
                  </div>
                  <div className="clientui-actions">
                    <button className="clientui-btn primary" onClick={() => setCurrentStep(2)}>Choisir ce client</button>
                    <button className="clientui-btn ghost" onClick={() => setCurrentStep(1)}>Voir la fiche</button>
                  </div>
                </>
              ) : (
                <div className="helper">Choisissez un client à gauche pour voir les détails</div>
              )}
            </div>
          </div>
        </div>

        {/* Étape 2: Sélection des sections */}
        {selectedClient && (
          <div className={`form-step ${currentStep === 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
            <h3>2. Sélection des sections</h3>
            {currentStep > 2 && selectedSections.length > 0 && (
              <div className="selected-sections-info">
                <div className="info-badge">
                  ✅ {selectedSections.length} section(s) sélectionnée(s)
                </div>
              </div>
            )}
            
            {/* Nouveau design pour la sélection des sections */}
            <div className="sections-selection-container">
              <div className="sections-list">
                {sectionTypes.map((section) => {
                  const isSelected = selectedSections.some(s => s.id === section.id);
                  // const hasSousSections = sousSections[section.id] && sousSections[section.id].length > 0; // Non utilisé pour le moment
                  const isExpanded = expandedSections.has(section.id);
                  
                  return (
                    <div key={section.id} className="section-item-wrapper">
                      <div 
                        className={`section-item ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="section-item-header">
                          <div className="section-checkbox">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSectionToggle(section)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="section-title">
                            {section.description}
                          </div>
                          <div 
                            className="section-arrow"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSectionExpand(section);
                            }}
                            title="Cliquer pour développer/réduire"
                          >
                            {isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
                          </div>
                        </div>
                        
                        {isSelected && (
                          <div className="section-remarque">
                            <textarea
                              placeholder="Ajouter une remarque pour cette section..."
                              value={getRemarque('sections', section.id)}
                              onChange={(e) => handleRemarqueChange('sections', section.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Sous-sections expandables */}
                      {isExpanded && (
                        <div className="sous-sections-container">
                          {sousSections[section.id]?.map((sousSection) => {
                            const isSousSelected = selectedSousSections.some(ss => ss.id === sousSection.id);
                            const isPriceSection = section.description.includes('Bordereau de prix') || section.description.includes('Prix');
                            const isSpecialSection = section.description.includes('Délai de réalisation') || 
                                                   section.description.includes('Conditions de paiement') || 
                                                   section.description.includes('Validité de l\'offre');
                            
                            return (
                              <div key={sousSection.id} className="sous-section-item-wrapper">
                                <div 
                                  className={`sous-section-item ${isSousSelected ? 'selected' : ''}`}
                                >
                                  <div className="sous-section-header">
                                    <div className="sous-section-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={isSousSelected}
                                        onChange={() => handleSousSectionToggle(sousSection, section)}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>
                                    <div className="sous-section-title">
                                      {sousSection.description}
                                    </div>
                                    <div 
                                      className="sous-section-arrow"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSousSectionExpand(sousSection, section);
                                      }}
                                      title="Cliquer pour développer/réduire"
                                    >
                                      {expandedSousSections.has(sousSection.id) ? <CaretDown size={14} /> : <CaretRight size={14} />}
                                    </div>
                                  </div>
                                  
                                  {isSousSelected && (
                                    <>
                                      {isSpecialSection && (
                                        <div className="sous-section-value-input">
                                          <input
                                            type="text"
                                            placeholder="Saisir la valeur..."
                                            value={getSousSectionValue(sousSection.id)}
                                            onChange={(e) => handleSousSectionValueChange(sousSection.id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </div>
                                      )}
                                      <div className="sous-section-remarque">
                                        <textarea
                                          placeholder="Ajouter une remarque pour cette sous-section..."
                                          value={getRemarque('sousSections', sousSection.id)}
                                          onChange={(e) => handleRemarqueChange('sousSections', sousSection.id, e.target.value)}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </div>
                                    </>
                                  )}
                                </div>
                                
                                {/* Tableau de prix pour les sections de prix */}
                                {isPriceSection && isSousSelected && (
                                  <div className="price-table-container">
                                    <table className="price-table">
                                      <thead>
                                        <tr>
                                          <th>Désignation</th>
                                          <th>Unité</th>
                                          <th>QT</th>
                                          <th>PU (CHF) HT</th>
                                          <th>PT (CHF) HT</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td>{sousSection.description}</td>
                                          <td>{sousSection.unite || 'M²'}</td>
                                          <td>
                                            <input 
                                              type="number" 
                                              className="price-input" 
                                              placeholder="0.00"
                                              value={priceData[sousSection.id]?.qt || ''}
                                              onChange={(e) => handlePriceInputChange(sousSection.id, 'qt', e.target.value)}
                                            />
                                          </td>
                                          <td>
                                            <input 
                                              type="number" 
                                              className="price-input" 
                                              placeholder="0.00"
                                              value={priceData[sousSection.id]?.pu || ''}
                                              onChange={(e) => handlePriceInputChange(sousSection.id, 'pu', e.target.value)}
                                            />
                                          </td>
                                          <td className="price-total">
                                            {calculateTotal(sousSection.id)}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                                
                                {/* Items pour les sections non-prix et non-spéciales */}
                                {!isPriceSection && !isSpecialSection && expandedSousSections.has(sousSection.id) && (
                                  <div className="items-container">
                                    {items[sousSection.id]?.map((item) => {
                                      const isItemSelected = selectedItems.some(i => i.id === item.id);
                                      return (
                                        <div key={item.id} className="item-wrapper">
                                          <div 
                                            className={`item-item ${isItemSelected ? 'selected' : ''}`}
                                          >
                                            <div className="item-header">
                                              <div className="item-checkbox">
                                                <input
                                                  type="checkbox"
                                                  checked={isItemSelected}
                                                  onChange={() => handleItemToggle(item, sousSection, section)}
                                                  onClick={(e) => e.stopPropagation()}
                                                />
                                              </div>
                                              <div className="item-title">
                                                {item.description}
                                              </div>
                                              <div 
                                                className="item-arrow"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleItemExpand(item, sousSection, section);
                                                }}
                                                title="Cliquer pour développer/réduire"
                                              >
                                                {expandedItems.has(item.id) ? <CaretDown size={12} /> : <CaretRight size={12} />}
                                              </div>
                                            </div>
                                            
                                            {isItemSelected && (
                                              <div className="item-remarque">
                                                <textarea
                                                  placeholder="Ajouter une remarque pour cet item..."
                                                  value={getRemarque('items', item.id)}
                                                  onChange={(e) => handleRemarqueChange('items', item.id, e.target.value)}
                                                  onClick={(e) => e.stopPropagation()}
                                                />
                                              </div>
                                            )}
                                          </div>
                                          
                                          {/* Sous-items */}
                                          {expandedItems.has(item.id) && (
                                            <div className="sous-items-container">
                                              {sousItems[item.id]?.map((sousItem) => {
                                                const isSousItemSelected = selectedSousItems.some(si => si.id === sousItem.id);
                                                return (
                                                  <div key={sousItem.id} className="sous-item-wrapper">
                                                    <div 
                                                      className={`sous-item-item ${isSousItemSelected ? 'selected' : ''}`}
                                                    >
                                                      <div className="sous-item-header">
                                                        <div className="sous-item-checkbox">
                                                          <input
                                                            type="checkbox"
                                                            checked={isSousItemSelected}
                                                            onChange={() => handleSousItemToggle(sousItem, item, sousSection, section)}
                                                            onClick={(e) => e.stopPropagation()}
                                                          />
                                                        </div>
                                                        <div className="sous-item-title">
                                                          {sousItem.description}
                                                        </div>
                                                        <div className="sous-item-unite">
                                                          <input
                                                            type="text"
                                                            placeholder="Valeur (ex: 380)"
                                                            value={unites[sousItem.id] || ""}
                                                            onChange={(e) => handleUniteChange(sousItem.id, e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                          />
                                                        </div>
                                                      </div>
                                                      
                                                      {isSousItemSelected && (
                                                        <div className="sous-item-remarque">
                                                          <textarea
                                                            placeholder="Ajouter une remarque pour ce sous-item..."
                                                            value={getRemarque('sousItems', sousItem.id)}
                                                            onChange={(e) => handleRemarqueChange('sousItems', sousItem.id, e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                          />
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Étape 3: Configuration des détails */}
        {selectedClient && selectedSections.length > 0 && (
          <div className={`form-step ${currentStep === 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
            <h3>3. Configuration des détails</h3>
            {currentStep > 3 && selectedSousSections.length > 0 && (
              <div className="selected-sous-sections-info">
                <div className="info-badge">
                  ✅ {selectedSousSections.length} sous-section(s) sélectionnée(s)
                </div>
              </div>
            )}
            
            {/* Configuration hiérarchique des sections sélectionnées */}
            {selectedSections.map(section => {
              const sectionSousSections = sousSections[section.id] || [];
              const isPriceSection = section.description.includes('Bordereau de prix') || section.description.includes('Prix');
              
              return (
                <div key={section.id} className="section-configuration">
                  <h4>Configuration de "{section.description}"</h4>
                  
                  {isPriceSection ? (
                    // Affichage en tableau pour les sections de prix
                    <div className="price-table-container">
                      <table className="price-table">
                        <thead>
                          <tr>
                            <th>Désignation</th>
                            <th>Unité</th>
                            <th>QT</th>
                            <th>PU DHS HT</th>
                            <th>PT DHS HT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sectionSousSections.map((sousSection) => {
                            const isSelected = selectedSousSections.some(ss => ss.id === sousSection.id);
                            return (
                              <tr key={sousSection.id} className={isSelected ? 'selected-row' : ''}>
                                <td>
                                  <div className="price-designation">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleSousSectionToggle(sousSection, section)}
                                    />
                                    <span>{sousSection.description}</span>
                                  </div>
                                </td>
                                <td>{sousSection.unite || 'M²'}</td>
                                <td>
                                  <input 
                                    type="number" 
                                    className="price-input" 
                                    placeholder="0.00"
                                    value={priceData[sousSection.id]?.qt || ''}
                                    onChange={(e) => handlePriceInputChange(sousSection.id, 'qt', e.target.value)}
                                    disabled={!isSelected}
                                  />
                                </td>
                                <td>
                                  <input 
                                    type="number" 
                                    className="price-input" 
                                    placeholder="0.00"
                                    value={priceData[sousSection.id]?.pu || ''}
                                    onChange={(e) => handlePriceInputChange(sousSection.id, 'pu', e.target.value)}
                                    disabled={!isSelected}
                                  />
                                </td>
                                <td className="price-total">
                                  {isSelected ? calculateTotal(sousSection.id) : '0.00'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    // Affichage hiérarchique pour les autres sections
                    <div className="hierarchical-configuration">
                      {sectionSousSections.map((sousSection) => {
                        const isSelected = selectedSousSections.some(ss => ss.id === sousSection.id);
                        const isExpanded = expandedSousSections.has(sousSection.id);
                        const isSpecialSection = section.description.includes('Délai de réalisation') || 
                                               section.description.includes('Conditions de paiement') || 
                                               section.description.includes('Validité de l\'offre');
                        const sousSectionItems = items[sousSection.id] || [];
                        
                        return (
                          <div key={sousSection.id} className="sous-section-config-item">
                            <div className="sous-section-config-header">
                              <div className="sous-section-checkbox">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleSousSectionToggle(sousSection, section)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="sous-section-config-title">
                                {sousSection.description}
                              </div>
                              {!isSpecialSection && sousSectionItems.length > 0 && (
                                <div
                                  className="sous-section-config-arrow"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSousSectionExpand(sousSection, section);
                                  }}
                                  title="Cliquer pour développer/réduire les items"
                                >
                                  {isExpanded ? '▼' : '▶'}
                                </div>
                              )}
                            </div>
                            
                            {/* Configuration pour les sous-sections spéciales */}
                            {isSelected && isSpecialSection && (
                              <div className="sous-section-config-content">
                                <div className="sous-section-value-input">
                                  <input
                                    type="text"
                                    placeholder="Saisir la valeur..."
                                    value={getSousSectionValue(sousSection.id)}
                                    onChange={(e) => handleSousSectionValueChange(sousSection.id, e.target.value)}
                                  />
                                </div>
                                <div className="remarque-input">
                                  <textarea
                                    placeholder="Ajouter une remarque pour cette sous-section..."
                                    value={getRemarque('sousSections', sousSection.id)}
                                    onChange={(e) => handleRemarqueChange('sousSections', sousSection.id, e.target.value)}
                                  />
                                </div>
                              </div>
                            )}
                            
                            {/* Items des sous-sections (pour les sections non-spéciales) */}
                            {!isSpecialSection && isExpanded && sousSectionItems.length > 0 && (
                              <div className="sous-section-items-config">
                                {sousSectionItems.map((item) => {
                                  const isItemSelected = selectedItems.some(i => i.id === item.id);
                                  const isItemExpanded = expandedItems.has(item.id);
                                  const itemSousItems = sousItems[item.id] || [];
                                  
                                  return (
                                    <div key={item.id} className="item-config-item">
                                      <div className="item-config-header">
                                        <div className="item-checkbox">
                                          <input
                                            type="checkbox"
                                            checked={isItemSelected}
                                            onChange={() => handleItemToggle(item, sousSection, section)}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </div>
                                        <div className="item-config-title">
                                          {item.type_valeur === 'indent' && <span className="indent">-</span>}
                                          {item.description}
                                        </div>
                                        {itemSousItems.length > 0 && (
                                          <div
                                            className="item-config-arrow"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleItemExpand(item, sousSection, section);
                                            }}
                                            title="Cliquer pour développer/réduire les sous-items"
                                          >
                                            {isItemExpanded ? '▼' : '▶'}
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Configuration pour les items sélectionnés */}
                                      {isItemSelected && (
                                        <div className="item-config-content">
                                          <div className="remarque-input">
                                            <textarea
                                              placeholder="Ajouter une remarque pour cet item..."
                                              value={getRemarque('items', item.id)}
                                              onChange={(e) => handleRemarqueChange('items', item.id, e.target.value)}
                                            />
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Sous-items des items */}
                                      {isItemExpanded && itemSousItems.length > 0 && (
                                        <div className="item-sous-items-config">
                                          {itemSousItems.map((sousItem) => {
                                            const isSousItemSelected = selectedSousItems.some(si => si.id === sousItem.id);
                                            
                                            return (
                                              <div key={sousItem.id} className="sous-item-config-item">
                                                <div className="sous-item-config-header">
                                                  <div className="sous-item-checkbox">
                                                    <input
                                                      type="checkbox"
                                                      checked={isSousItemSelected}
                                                      onChange={() => handleSousItemToggle(sousItem, item, sousSection, section)}
                                                      onClick={(e) => e.stopPropagation()}
                                                    />
                                                  </div>
                                                  <div className="sous-item-config-title">
                                                    <span className="indent">--</span>
                                                    {sousItem.description}
                                                  </div>
                                                  <div className="sous-item-unite">
                                                    <input
                                                      type="text"
                                                      placeholder="Valeur (ex: 380)"
                                                      value={unites[sousItem.id] || ""}
                                                      onChange={(e) => handleUniteChange(sousItem.id, e.target.value)}
                                                      onClick={(e) => e.stopPropagation()}
                                                    />
                                                  </div>
                                                </div>
                                                
                                                {/* Configuration pour les sous-items sélectionnés */}
                                                {isSousItemSelected && (
                                                  <div className="sous-item-config-content">
                                                    <div className="remarque-input">
                                                      <textarea
                                                        placeholder="Ajouter une remarque pour ce sous-item..."
                                                        value={getRemarque('sousItems', sousItem.id)}
                                                        onChange={(e) => handleRemarqueChange('sousItems', sousItem.id, e.target.value)}
                                                      />
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            {/* Configuration pour les sous-sections non-spéciales sélectionnées */}
                            {isSelected && !isSpecialSection && (
                              <div className="sous-section-config-content">
                                <div className="remarque-input">
                                  <textarea
                                    placeholder="Ajouter une remarque pour cette sous-section..."
                                    value={getRemarque('sousSections', sousSection.id)}
                                    onChange={(e) => handleRemarqueChange('sousSections', sousSection.id, e.target.value)}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Étape 4: En-tête du devis */}
        {selectedClient && selectedSections.length > 0 && (
          <div className={`form-step ${currentStep === 4 ? 'active' : ''} ${currentStep > 4 ? 'completed' : ''}`}>
            <h3>4. En-tête du devis</h3>
          <div className="devis-form">
            <div className="form-row">
              <div className="form-group">
                <label>N/Réf.</label>
                <div className="reference-input">
                  <input
                    type="text"
                    name="reference"
                    value={devisData.reference}
                    onChange={handleInputChange}
                    placeholder="Référence du devis"
                  />
                  <button type="button" onClick={handleGenerateReference} className="generate-btn">
                    Générer
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  name="date"
                  value={devisData.date}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Nombre de pages</label>
                <input
                  type="number"
                  name="nombrePages"
                  value={devisData.nombrePages}
                  onChange={handleInputChange}
                  min="1"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Destinataire</label>
                <input
                  type="text"
                  name="destinataire"
                  value={devisData.destinataire}
                  onChange={handleInputChange}
                  placeholder="Mr. Nom Prénom"
                />
              </div>
              <div className="form-group">
                <label>Société</label>
                <input
                  type="text"
                  name="societe"
                  value={devisData.societe}
                  onChange={handleInputChange}
                  placeholder="Nom de la société"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Tél</label>
                <input
                  type="text"
                  name="telephone"
                  value={devisData.telephone}
                  onChange={handleInputChange}
                  placeholder="Numéro de téléphone"
                />
              </div>
              <div className="form-group">
                <label>V/Réf.</label>
                <input
                  type="text"
                  name="vRef"
                  value={devisData.vRef}
                  onChange={handleInputChange}
                  placeholder="Votre référence"
                />
              </div>
            </div>

            <div className="form-group full-width">
              <label>Objet</label>
              <textarea
                name="objet"
                value={devisData.objet}
                onChange={handleInputChange}
                placeholder="Description des travaux ou services"
                rows="3"
              />
            </div>
          </div>
        </div>
        )}

        {/* Boutons de navigation */}
        <NavigationButtons />

        <Alert 
          message={message} 
          onClose={() => setMessage("")}
        />
      </div>
      
      {/* Composants de progression pour le chargement */}
      <LoadingProgress 
        isVisible={isLoadingPDF}
        message="Génération du PDF en cours..."
        showProgress={true}
        color="warning"
      />
      
      <LoadingProgress 
        isVisible={isLoadingWord}
        message="Génération du document Word en cours..."
        showProgress={true}
        color="primary"
      />
    </div>
  );
}

export default CreateDevis; 